import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Output,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, map, Observable } from 'rxjs';
import {
  LucideAngularModule,
  Sparkles,
  ArrowLeft,
  Check,
  Paperclip,
  ChevronDown,
  ChevronUp,
  Lightbulb,
} from 'lucide-angular';
import { AssignmentService } from '../../services/assignment.service';
import {
  ASSIGNMENT_THEME_COLOR,
  GENERATION_PROGRESS_STEPS,
  QUESTION_COUNT_OPTIONS,
} from '../../services/assignment.models';
import { AmplitudeService } from '../../../../../common/analytics/amplitude.service';

const VISIBLE_QUESTION_COUNTS = [5, 10, 15] as const;

interface ProgressStepDisplay {
  key: string;
  label: string;
  status: 'completed' | 'in_progress' | 'pending';
}

@Component({
  selector: 'app-step-objective',
  standalone: true,
  imports: [CommonModule, FormsModule, LucideAngularModule],
  templateUrl: './step-objective.html',
  styleUrl: './step-objective.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StepObjectiveComponent implements AfterViewInit {
  private readonly assignmentService = inject(AssignmentService);
  private readonly amplitudeService = inject(AmplitudeService);
  private readonly destroyRef = inject(DestroyRef);

  @ViewChild('objectiveTextarea') objectiveTextarea!: ElementRef<HTMLTextAreaElement>;

  @Output() readonly next = new EventEmitter<void>();
  @Output() readonly back = new EventEmitter<void>();

  protected readonly themeColor = ASSIGNMENT_THEME_COLOR;
  protected readonly inputData$ = this.assignmentService.inputData$;
  protected readonly error$ = this.assignmentService.error$;
  protected readonly isGenerating$ = this.assignmentService.isGenerating$;
  protected readonly icons = {
    Sparkles,
    ArrowLeft,
    Check,
    Paperclip,
    ChevronDown,
    ChevronUp,
    Lightbulb,
  };

  // ============================================
  // EXAMPLE OBJECTIVE
  // ============================================

  protected readonly isLoadingExample$$ = new BehaviorSubject<boolean>(false);
  protected readonly isLoadingExample$ =
    this.isLoadingExample$$.asObservable();

  protected readonly exampleUsed$$ = new BehaviorSubject<boolean>(false);
  protected readonly exampleUsed$ = this.exampleUsed$$.asObservable();

  // ============================================
  // QUESTION COUNT (block selection with show more)
  // ============================================

  private readonly showMoreQuestionCounts$$ = new BehaviorSubject<boolean>(
    false,
  );
  protected readonly showMoreQuestionCounts$ =
    this.showMoreQuestionCounts$$.asObservable();

  protected readonly visibleQuestionCounts$: Observable<readonly number[]> =
    this.showMoreQuestionCounts$.pipe(
      map((showMore) => {
        if (showMore) {
          return QUESTION_COUNT_OPTIONS;
        }
        return QUESTION_COUNT_OPTIONS.filter((opt) =>
          (VISIBLE_QUESTION_COUNTS as readonly number[]).includes(opt),
        );
      }),
    );

  protected readonly hasHiddenQuestionCounts: boolean =
    QUESTION_COUNT_OPTIONS.some(
      (opt) =>
        !(VISIBLE_QUESTION_COUNTS as readonly number[]).includes(opt),
    );

  // Check if can proceed
  protected readonly canProceed$: Observable<boolean> = this.inputData$.pipe(
    map((data) => !!data.objective?.trim()),
  );

  // Generation progress
  protected readonly generationProgressStep$ =
    this.assignmentService.generationProgressStep$;

  // Calculate progress percentage
  protected readonly progressPercentage$: Observable<number> =
    this.generationProgressStep$.pipe(
      map((step) => {
        const totalSteps = GENERATION_PROGRESS_STEPS.length;
        return Math.round((step / totalSteps) * 100);
      })
    );

  // Get display steps with their status
  protected readonly displaySteps$: Observable<ProgressStepDisplay[]> =
    this.generationProgressStep$.pipe(
      map((currentStep) => {
        return GENERATION_PROGRESS_STEPS.map((step, index) => ({
          key: step.key,
          label: step.label,
          status:
            index < currentStep
              ? 'completed'
              : index === currentStep
                ? 'in_progress'
                : 'pending',
        }));
      })
    );

  // ============================================
  // TEXTAREA AUTO-RESIZE
  // ============================================

  ngAfterViewInit(): void {
    // Auto-resize on initial load if there's existing content
    this.autoResizeTextarea();
  }

  private autoResizeTextarea(): void {
    const el = this.objectiveTextarea?.nativeElement;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }

  // ============================================
  // OBJECTIVE INPUT
  // ============================================

  protected updateObjective(event: Event): void {
    const textarea = event.target as HTMLTextAreaElement;
    this.assignmentService.updateInputData({ objective: textarea.value });
    this.autoResizeTextarea();
  }

  // ============================================
  // QUESTION COUNT
  // ============================================

  protected selectQuestionCount(count: number): void {
    this.assignmentService.updateInputData({ questionCount: count });
  }

  protected toggleShowMoreQuestionCounts(): void {
    this.showMoreQuestionCounts$$.next(!this.showMoreQuestionCounts$$.value);
  }

  // ============================================
  // EXAMPLE OBJECTIVE
  // ============================================

  protected onUseExample(): void {
    this.amplitudeService.trackEvent(
      'assignment_wizard_use_example_objective_clicked',
      {
        assignment_id: this.assignmentService.assignmentId,
        grade: this.assignmentService.inputData.grade,
        tier1_subject_id: this.assignmentService.inputData.tier1SubjectId,
        tier2_subject_id: this.assignmentService.inputData.tier2SubjectId,
      },
    );

    this.isLoadingExample$$.next(true);
    this.assignmentService
      .generateExampleObjective()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isLoadingExample$$.next(false);
          this.exampleUsed$$.next(true);
          // Allow Angular to update the textarea value, then auto-resize
          setTimeout(() => this.autoResizeTextarea());
        },
        error: (err: any) => {
          this.isLoadingExample$$.next(false);
          this.amplitudeService.trackEvent('assignment_wizard_error', {
            assignment_id: this.assignmentService.assignmentId,
            error_type: 'example_objective_failed',
            error: err?.message || 'Unknown error',
            step: 2,
          });
        },
      });
  }

  // ============================================
  // ATTACH FILES (placeholder)
  // ============================================

  protected onAttachFiles(): void {
    // TODO: Implement file attachment
  }

  // ============================================
  // NAVIGATION
  // ============================================

  protected onBack(): void {
    this.back.emit();
  }

  private generationStartTime: number | null = null;

  protected onGenerateAndReview(): void {
    const inputData = this.assignmentService.inputData;
    this.amplitudeService.trackEvent(
      'assignment_wizard_step_objective_completed',
      {
        assignment_id: this.assignmentService.assignmentId,
        question_count: inputData.questionCount,
        objective_length: inputData.objective?.length ?? 0,
      },
    );

    this.generationStartTime = Date.now();

    // Subscribe to start the generation - this immediately updates status and isGenerating$
    this.assignmentService
      .updateObjectiveAndGenerateQuestions()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (questions) => {
          const durationMs = this.generationStartTime
            ? Date.now() - this.generationStartTime
            : null;

          this.amplitudeService.trackEvent('assignment_generation_completed', {
            assignment_id: this.assignmentService.assignmentId,
            duration_ms: durationMs,
            questions_generated: questions.length,
          });

          // Generation complete - navigate to questions page
          // Note: Don't emit next here as setStep already handles navigation
          // and emitting next would call nextStep() which increments to step 4
          this.assignmentService.setStep(3);
        },
        error: (err: any) => {
          this.amplitudeService.trackEvent('assignment_wizard_error', {
            assignment_id: this.assignmentService.assignmentId,
            error_type: 'generation_failed',
            error: err?.message || 'Unknown error',
            step: 2,
          });
        },
      });
  }
}
